import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { withTracking } from '@adac-mp/components/src/utils/tracking/tracking';
import {
	ArrowRight,
	Checkbox,
	Drawer,
	FlagAustria,
	FlagSlovenia,
	FlagSchweiz,
	Head,
	HintMessage,
	IconButton,
	Richtext,
	SkeletonTemplate,
	Typography,
	Wrapper,
	getDrawerTitle,
	getHintContent,
	getOneCartCountries,
	renderErrorMessage,
} from '@adac-mp/components';
import {
	ISelectedCountry,
	ITypeRichTextFields,
	ITypeVignetteOrderingProcessFields,
	IVignetteContext,
	OfferContext,
	TypeLandEnumValues,
	VignetteContext,
	countrySupplierMapByLandId,
	getUrlSearchParamsAsStack,
	isoCodeByLandId,
} from '@adac-mp/shared';
import { CountrySelectionDrawer } from './countrySelectionDrawer';

interface ICountrySelectionProps {
	hasError?: boolean;
	updateStepCount?: React.Dispatch<React.SetStateAction<number>>;
	updateValidatedStepsMap?: (hasError: boolean, reset?: boolean) => void;
}

const CountrySelectionComponent = ({
	hasError,
	updateStepCount = () => {},
	updateValidatedStepsMap = () => {},
}: ICountrySelectionProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const {
		selectedCountries,
		selectedProducts,
		contentfulLabels,
		vehicleCategory,
		initSelectedCountries,
		updateSelectedCountries,
		removeSelectedProductsBySupplierId,
	} = useContext(VignetteContext);
	const { updateSelectedProductsDates } = useContext<IVignetteContext>(VignetteContext);
	const { allOffers } = useContext(OfferContext);
	const [drawerIsOpen, setDrawerIsOpen] = useState<boolean>(false);
	const countryLabels = contentfulLabels as ITypeVignetteOrderingProcessFields;

	const isSchweizDeactivated = allOffers
		.filter((offer) => offer.supplierId === 14)
		.some((offer) => offer.deactivated);
	const isSchweizAllowed = !isSchweizDeactivated || searchParams.get('enableBAZG') === 'true';

	const allCountries = getOneCartCountries(countryLabels?.oneCartCountries?.items);
	const countries = allCountries?.filter((country) => isSchweizAllowed || country.countryId !== 'schweiz');

	const renderHeadComponent = () => (
		<Head headline={countryLabels?.oneCartHeadline || 'Durch welche Länder reisen Sie?'} />
	);

	const renderCountryIconById = (countryId: TypeLandEnumValues) => {
		if (countryId === 'oesterreich') {
			return <FlagAustria />;
		}
		if (countryId === 'vignette-svn') {
			return <FlagSlovenia />;
		}
		if (countryId === 'schweiz') {
			return <FlagSchweiz />;
		}
		return <></>;
	};

	const renderCountryCard = (countryId: TypeLandEnumValues, country: string, description: ITypeRichTextFields) => (
		<span className="flex flex-col items-center justify-center text-center gap-2">
			{renderCountryIconById(countryId)}
			<Typography size="lg" variant="title" color="title">
				{country}
			</Typography>
			<Typography color="lightText">
				<Richtext document={description} />
			</Typography>
		</span>
	);

	const isCountrySelected = (countryId: TypeLandEnumValues) =>
		Boolean(selectedCountries.find(({ supplierId }) => supplierId === countrySupplierMapByLandId[countryId]));

	useEffect(() => {
		updateValidatedStepsMap(Boolean(selectedCountries.length));
	}, [selectedCountries]);

	useEffect(() => {
		const urlPreSelectedCountries = getUrlSearchParamsAsStack('countries', searchParams);
		const preSelectedCountries = countries?.filter(({ countryId }) => {
			return urlPreSelectedCountries.includes(isoCodeByLandId[countryId]);
		});
		if (preSelectedCountries) {
			const initCountries = preSelectedCountries.map(
				({ countryId, country }): ISelectedCountry => ({
					supplierId: countrySupplierMapByLandId[countryId],
					isoCode: isoCodeByLandId[countryId],
					name: country,
				}),
			);
			initSelectedCountries(initCountries);
		}
	}, []);

	const updateCountriesUrl = (newCountries: ISelectedCountry[]) => {
		const countryNames = newCountries.map(({ isoCode }) => isoCode);
		setSearchParams(
			(params) => {
				params.set('countries', countryNames.join(','));
				return params;
			},
			{ preventScrollReset: true, replace: true },
		);
	};

	const isSlovenianPkwSelected =
		selectedCountries.find(({ isoCode }) => isoCode === 'svn') && vehicleCategory?.id === 2;

	return (
		<Wrapper animateFirstStep paddingTop="large">
			{renderHeadComponent()}
			{hasError &&
				renderErrorMessage({
					message: countryLabels?.oneCartCountryError || 'Bitte wählen Sie ein Land aus.',
					className: `w-full !self-center mb-5 md:w-6/12`,
				})}
			{countries ? (
				<div
					className={`flex flex-col gap-4 md:self-center ${
						countries.length >= 3 ? 'md:w-10/12' : 'md:w-6/12'
					}`}>
					<div
						className={`grid grid-cols-2 grid-rows-1 gap-x-1.5 gap-y-3 md:gap-5 md:grid-cols-${
							countries.length > 3 ? 3 : countries.length
						}`}>
						{countries.map(({ countryId, country, description }) => (
							<Checkbox
								key={countryId}
								data-cy={`checkbox: ${countryId}`}
								rootClassName="!self-center"
								className="relative rounded h-full bg-white border-b-gray-600 flex-col-reverse flex-grow items-center justify-between py-5 px-4 gap-4"
								label={renderCountryCard(countryId, country, description)}
								title={country}
								checked={isCountrySelected(countryId)}
								setChecked={(checked) => {
									const supplierId = countrySupplierMapByLandId[countryId];
									const newCountries = updateSelectedCountries({
										supplierId,
										name: country,
										isoCode: isoCodeByLandId[countryId],
									});
									updateCountriesUrl(newCountries);
									if (checked) {
										updateStepCount((prev) => {
											if (prev > 3) return prev + 1;
											return prev;
										});
									}
									if (!checked && selectedProducts.length) {
										removeSelectedProductsBySupplierId(supplierId);
										updateStepCount((prev) => prev - 1);
									}
									if (selectedProducts.length > 0) {
										updateSelectedProductsDates(undefined);
									}
								}}
							/>
						))}
					</div>
					{isSlovenianPkwSelected && (
						<HintMessage
							className="mt-4 mb-2 transition-all duration-300 animate-fadeSlideIn"
							hints={getHintContent('Slowenien Transporter', [], countryLabels.hintMessages)}
						/>
					)}
					<IconButton
						className="border-none group w-fit inline-flex self-center gap-2 justify-center font-bold text-base items-center focus-visible:outline-primary text-gray-200 hover:text-gray-100"
						tracking={{
							info_click: {
								label: countryLabels?.oneCartOverviewPrices || 'Übersicht Mautprodukte & Preise',
								element: 'Länderauswahl',
								category: 'Content',
							},
						}}
						onClick={() => setDrawerIsOpen(true)}>
						<ArrowRight className="group-hover:[&>*]:stroke-gray-100" />
						{countryLabels?.oneCartOverviewPrices || 'Übersicht Mautprodukte & Preise'}
					</IconButton>
				</div>
			) : (
				<SkeletonTemplate fullWidth rowNumber={2} type="card" />
			)}
			<Drawer
				title={getDrawerTitle('countrySelection', countryLabels?.helpDrawers) || 'Mautprodukte & Preise'}
				paddingX="small"
				isOpen={drawerIsOpen}
				setIsOpen={setDrawerIsOpen}>
				<CountrySelectionDrawer id="countrySelection" />
			</Drawer>
		</Wrapper>
	);
};

export const CountrySelection = withTracking(CountrySelectionComponent);
